import React from 'react';
import { t } from '@lingui/core/macro';
import { FieldRow, OrgUnitSelect, SectionCard, SectionSpan, SelectField, TextField } from 'src/components/forms';
import { OrgUnitItem } from 'src/components/forms/lookups';
import { ORG_UNIT_TYPE_COLORS } from 'src/util/orgUnitTypeColors';
import { OrgUnitTypeRecord, allowedParentTypeNames, deriveGroupingCode, typeBehaviour } from 'src/util/orgUnitTypes';
import { OrgUnitRecord } from './orgUnitModel';

export type GeneralSectionProps = {
  draft: OrgUnitRecord;
  setField: (path: string, value: unknown) => void;
  errorFor: (path: string) => string | undefined;
  types: OrgUnitTypeRecord[];
  type: OrgUnitTypeRecord | null;
  lockType: boolean;
  parentLabel: string;
  units: OrgUnitItem[];
  unitsLoading: boolean;
  parent: OrgUnitItem | null;
  onTypeChange: (typeId: number | null) => void;
  span: SectionSpan;
};

const TYPE_BADGE = { path: 'organizationalUnitType.name', colors: ORG_UNIT_TYPE_COLORS };

export function GeneralSection(props: GeneralSectionProps) {
  const { draft, setField, errorFor, types, type, lockType, span } = props;
  const behaviour = typeBehaviour(type?.name);

  return (
    <SectionCard span={span} title={t`General`} hint={t`Name and where it sits in the hierarchy`}>
      <FieldRow>
        <TextField id="orgunit-name" label={t`Name`} required width="lg" value={draft.name} error={errorFor('name')} onChange={value => setField('name', value)} />
        {lockType ? (
          <TextField id="orgunit-type" label={t`Organizational Unit Type`} width="sm" value={type?.name ?? ''} readOnly onChange={() => undefined} />
        ) : (
          <SelectField
            id="orgunit-type"
            label={t`Organizational Unit Type`}
            required
            width="sm"
            options={types.map(option => ({ value: option.id as number, label: option.name }))}
            value={draft.organizationalUnitTypeId}
            error={errorFor('organizationalUnitTypeId')}
            onChange={value => props.onTypeChange(value === null ? null : Number(value))}
          />
        )}
        {behaviour.code === 'typed' ? (
          <TextField
            id="orgunit-code"
            label={t`Business Code`}
            required
            width="xs"
            value={draft.code}
            help={t`2 or 3 capital letters.`}
            error={errorFor('code')}
            onChange={value => setField('code', value.toUpperCase())}
          />
        ) : null}
        <ParentField {...props} />
        {behaviour.code === 'derived' ? (
          <TextField
            id="orgunit-code"
            label={t`Business Code`}
            width="sm"
            readOnly
            value={deriveGroupingCode(props.parent?.code, draft.name)}
            help={t`Derived: the parent's code, then the first three letters and the last letter of the name.`}
            onChange={() => undefined}
          />
        ) : null}
      </FieldRow>
    </SectionCard>
  );
}

/** A type with no allowed parents sits at the top: no picker, since an empty type filter would allow any unit. */
function ParentField({ draft, setField, errorFor, types, type, parentLabel, units, unitsLoading }: GeneralSectionProps) {
  if (!type) {
    return <OrgUnitSelect id="orgunit-parent" label={parentLabel} value={null} onChange={() => undefined} units={units} disabled help={t`Choose a type first.`} />;
  }

  const allowedNames = allowedParentTypeNames(type, types);
  if (allowedNames.length === 0) {
    return <TextField id="orgunit-parent" label={parentLabel} width="md" readOnly value={t`Top level (no parent)`} onChange={() => undefined} />;
  }

  return (
    <OrgUnitSelect
      id="orgunit-parent"
      label={parentLabel}
      required
      value={draft.parentId}
      units={units}
      unitsLoading={unitsLoading}
      selectableTypes={allowedNames}
      infoBadge={TYPE_BADGE}
      help={t`Can sit under: ${allowedNames.join(', ')}`}
      error={errorFor('parentId')}
      onChange={value => setField('parentId', value)}
    />
  );
}

export default GeneralSection;
